import {BaseQueryApi, FetchArgs} from '@reduxjs/toolkit/query';
import {fetchAuthSession} from 'aws-amplify/auth';
import {HttpStatusCode} from 'axios';

import {AUTH_EVENTS} from '@/app/auth/events/AuthEvents';
import {unSetUser} from '@/app/store/authSlice';
import {refreshAccessToken, unSetTokenData} from '@/app/store/tokenSlice';
import {wafProtectionService} from '@/services/wafProtection/WafProtectionService';

import {sessionExpiredResult} from './constants';
import {trackUserUnauthorized, trackWafEvent} from './trackers';

export interface ReAuthArgs {
  args: string | FetchArgs;
  api: BaseQueryApi;
  baseUrl: string;
  baseQuery: (arg0: any) => any;
}

export const logOut = (api: BaseQueryApi) => {
  api.dispatch(unSetTokenData());
  api.dispatch(unSetUser(null));
};

export const refreshUserSession = async ({
  args,
  api,
  baseUrl,
  baseQuery,
}: ReAuthArgs) => {
  const {idToken} = (await fetchAuthSession({forceRefresh: true})).tokens ?? {};

  if (idToken) {
    api.dispatch(refreshAccessToken(idToken?.toString() || ''));
    return await (
      await baseQuery(baseUrl)
    )(args, api);
  }

  logOut(api);
  return sessionExpiredResult;
};

export const handleWafResponse = async (
  url: string,
  response: Response | null,
): Promise<void> => {
  let initialEvent: Response | null = response;

  if (response && response.ok) {
    if (wafProtectionService.isChallenge(url, response)) {
      trackWafEvent(url, response, AUTH_EVENTS.WAF_CHALLENGE);
    }
    initialEvent = response;
  }

  if (
    wafProtectionService.isModakUrl(url) &&
    (!initialEvent || initialEvent?.status !== 202)
  ) {
    wafProtectionService.resetWafCount();
  }
};

export const handleHttpErrorResponse = (
  error: any,
  request: {
    url: string;
    method: string;
    body: any;
    headers: Headers;
  },
) => {
  if (
    (error?.status === HttpStatusCode.Forbidden &&
      wafProtectionService.hasModakWaf(error.headers)) ||
    error?.status === HttpStatusCode.TooManyRequests
  ) {
    trackWafEvent(request.url, error, AUTH_EVENTS.WAF_BLOCKED);
  } else {
    wafProtectionService.resetWafCount();
  }

  if (error?.status === HttpStatusCode.Unauthorized) {
    trackUserUnauthorized();
  }

  throw error;
};

export const normalizeHeaders = (
  headers: Record<string, string> | Headers | string[][] | undefined,
): Headers => {
  const normalizedHeaders = new Headers();

  if (!headers) return normalizedHeaders;

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      normalizedHeaders.set(key, value);
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      normalizedHeaders.set(key, value);
    });
  } else {
    Object.entries(headers).forEach(([key, value]) => {
      normalizedHeaders.set(key, value);
    });
  }

  return normalizedHeaders;
};
